import React from 'react';
import {Link} from 'react-router-dom';
import PlanBadge, {aiQuotaLine} from './PlanBadge';
import {useSubscription} from '../hooks/useSubscription';
import {formatDate} from '../utils/dateTime';

/**
 * Карточка «Тарифный план» в настройках профиля (CTHS-342).
 *
 * На `xs`/`sm` аватар в шапке не раскрывает меню, а уводит на профиль — значит тариф
 * должен быть виден здесь. На `md+` та же информация уже есть в дропдауне `UserAvatar`,
 * поэтому карточка скрыта (`md:hidden`): дублировать один и тот же факт в двух местах
 * одного экрана незачем.
 *
 * Сбой загрузки подписки не ломает остальную страницу профиля — карточка показывает
 * нейтральное сообщение с повтором.
 */
const ProfilePlanCard: React.FC = () => {
    const {subscription, loading, error, reload} = useSubscription();
    const quotaLine = aiQuotaLine(subscription?.quotas);
    const expiresAt = subscription?.expiresAt ? formatDate(subscription.expiresAt) : null;

    return (
        <section className="card p-6 md:hidden" data-testid="profile-plan-card">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Тарифный план
            </h3>

            {loading && <PlanBadge loading/>}

            {!loading && error && (
                <div data-testid="profile-plan-error">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        Не удалось загрузить данные подписки
                    </p>
                    <button type="button" onClick={reload} className="btn-secondary mt-3 min-h-[44px]">
                        Повторить
                    </button>
                </div>
            )}

            {!loading && !error && subscription && (
                <div className="space-y-2">
                    <PlanBadge
                        planCode={subscription.planCode}
                        planName={subscription.planName}
                        status={subscription.status}
                    />
                    {expiresAt && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">Действует до {expiresAt}</p>
                    )}
                    {quotaLine && (
                        <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="profile-plan-quota">
                            {quotaLine}
                        </p>
                    )}
                    {subscription.self ? (
                        <Link
                            to="/subscription"
                            data-testid="profile-plan-manage"
                            className="btn-secondary mt-2 min-h-[44px] inline-flex"
                        >
                            Управлять подпиской
                        </Link>
                    ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="profile-plan-member-note">
                            Тарифом управляет владелец семейной подписки.
                        </p>
                    )}
                </div>
            )}
        </section>
    );
};

export default ProfilePlanCard;
